<script setup lang="ts">
/**
 * Group table cell: display name (falling back to a placeholder, matching
 * `BookingTypeCard`'s `defaultTypeName` convention since the backend model
 * allows a null name) on the first line, "с <date> · <age>" underneath —
 * mirrors the sketch's `.g-desc`/`.g-started`.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatStartDate } from '@/utils/formatStartDate'

const props = defineProps<{
  name?: string | null
  description?: string | null
  /** "YYYY-MM-DD" group start date. */
  start: string
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
}>()

const { t } = useI18n()

const displayName = computed(() => props.name ?? props.description ?? t('cards.group.defaultName'))

const started = computed(() => formatStartDate({ date: props.start, timeZone: 'UTC' }))
</script>

<template>
  <td
    class="group-name-cell font-tufte text-tufte-text"
    :style="maxWidth ? { maxWidth: maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <div class="group-name-cell__name">{{ displayName }}</div>
    <div class="group-name-cell__started">
      {{
        t('cards.group.startedOn', { date: started.formattedDate, since: started.durationSince })
      }}
    </div>
  </td>
</template>

<style scoped>
.group-name-cell__name {
  font-size: var(--text-tufte-lg);
}

.group-name-cell__started {
  font-family: var(--font-tufte-sans);
  font-size: var(--text-tufte-xs);
  color: var(--color-tufte-muted);
  margin-top: 0.15rem;
}
</style>
